import React, { useState, useEffect } from 'react';
import { Button, Card, Form, Input, message, Modal, Popconfirm, Radio, Select, Table, Tooltip, Typography, Switch } from 'antd';
import { PlusOutlined, UnorderedListOutlined } from '@ant-design/icons';
import { searchDomains, viewDomainReq, domainChangeRequest } from './Domain.service';
import { PaginationConstant } from '../../common/constants';
import { BitOptions, COMMON_STRING, COMMON_MSG, COMMON_REGEX, ERROR_MSG, SIZE } from '../Helper/Constant';

const { Text, Link } = Typography;

const ModalTypes = {
    request: { key: 'request', title: 'Domain Request Form' },
    viewReq: { key: 'viewReq', title: 'View Domain Request', modalWidth: '60%' },
};

const RequestType = {
    ADD: 'ADD',
    EDIT: 'EDIT',
};
let USERNAME = null;

const Domain = ({ user }) => {
    USERNAME = user.email.split('@')[0];
    const [loading, setLoading] = useState(false);
    const [tableData, setTableData] = useState([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [modalType, setModalType] = useState(ModalTypes.request.key);
    const [domainRow, setDomainRow] = useState(null);
    const [exactMatch, setExactMatch] = useState(false);
    const tableBitView = (value) => {
        if (value === 0) return <Text type={'secondary'}>{value}</Text>;
        if (value === 1) return <Text type={'success'}>{value}</Text>;
        if (value === -1) return <Text type={'danger'}>{value}</Text>;
        return <Text>{value}</Text>;
    };
    const tableColumn = [
        {
            title: 'App Id',
            dataIndex: 'application_id',
            key: 'application_id',
            width: '6%',
            render: (value) => (
                <Link target={'_blank'} href={'/cciresearch/' + value}>
                    {value}
                </Link>
            ),
        },
        {
            title: 'App Name',
            dataIndex: 'Application',
            key: 'Application',
            render: (value) => <Text>{value?.current_name}</Text>,
        },
        {
            title: 'Domain Name',
            dataIndex: 'domain_name',
            key: 'domain_name',
        },
        {
            title: 'Skip Forwarder',
            dataIndex: 'b_skip_for_forwarder',
            key: 'b_skip_for_forwarder',
            render: tableBitView,
        },
        { title: 'Skip Ex-Proxy', dataIndex: 'b_skip_for_explicit_proxy', key: 'b_skip_for_explicit_proxy', render: tableBitView },
        { title: 'Skip Agent', dataIndex: 'b_skip_for_agent', key: 'b_skip_for_agent', render: tableBitView },
        { title: 'Is Login', dataIndex: 'is_login_domain', key: 'is_login_domain', render: tableBitView },
        { title: 'Discovery', dataIndex: 'additional_discovery_domain', key: 'additional_discovery_domain', render: tableBitView },
        { title: 'Skip Mapping', dataIndex: 'skip_app_mapping', key: 'skip_app_mapping', render: tableBitView },
        {
            title: 'Action',
            dataIndex: 'domain_id',
            key: 'domain_id',
            render: (value, row) => (
                <Link
                    className='ns-margin-right'
                    onClick={() => {
                        setModalType(ModalTypes.request.key);
                        setDomainRow({ ...row, req_type: RequestType.EDIT });
                        setModalOpen(true);
                    }}
                >
                    Request
                </Link>
            ),
        },
    ];

    const onDomainSearch = async (value, byAppId = false) => {
        setLoading(true);
        await searchDomains(value, exactMatch, byAppId).then((res) => {
            if (res) {
                setTableData(res.map((row) => row));
            }
        });
        setLoading(false);
    };

    const closeModal = () => {
        setModalOpen(false);
    };

    return (
        <div className='ns-margin-small'>
            <Card
                title='Search Domains'
                size='small'
                headStyle={{ backgroundColor: '#F0F8FF', border: 0 }}
                extra={
                    <div className='ns-flex ns-flex_gap'>
                        <Tooltip title={'Add Domain'}>
                            <PlusOutlined
                                style={{ cursor: 'pointer' }}
                                onClick={() => {
                                    setModalType(ModalTypes.request.key);
                                    setDomainRow({ req_type: 'ADD' });
                                    setModalOpen(true);
                                }}
                            />
                        </Tooltip>
                        <Tooltip title={'Domain Request'}>
                            <UnorderedListOutlined
                                style={{ cursor: 'pointer' }}
                                onClick={() => {
                                    setModalType(ModalTypes.viewReq.key);
                                    setModalOpen(true);
                                }}
                            />
                        </Tooltip>
                        <Input.Search
                            key={'search_by_app_id'}
                            size={'small'}
                            placeholder='Application Id'
                            style={{ width: '150px' }}
                            onSearch={(value) => onDomainSearch(value, true)}
                            loading={loading}
                        />
                        <Input.Search
                            key={'search_domain_input'}
                            size={'small'}
                            placeholder='Search Domains'
                            style={{ width: '300px' }}
                            onSearch={(value) => onDomainSearch(value)}
                            loading={loading}
                        />
                        <Text>Exact Match</Text>
                        <Switch onChange={(checked) => setExactMatch(checked)} />
                    </div>
                }
            >
                <Table
                    key={'domain_table'}
                    loading={loading}
                    rowKey={'domain_id'}
                    size='small'
                    bordered
                    columns={tableColumn}
                    dataSource={tableData}
                    pagination={PaginationConstant}
                />
            </Card>
            <Modal
                title={ModalTypes[modalType].title}
                open={modalOpen}
                onCancel={closeModal}
                footer={null}
                width={ModalTypes[modalType]?.modalWidth ?? '40%'}
                destroyOnClose
            >
                <>
                    {modalType === ModalTypes.request.key && <DomainReq initValues={domainRow} closeModal={closeModal} />}
                    {modalType === ModalTypes.viewReq.key && <ViewDomainRequests />}
                </>
            </Modal>
        </div>
    );
};

const ViewDomainRequests = () => {
    const tableColumn = [
        {
            title: 'Request Id',
            dataIndex: 'id',
            key: 'id',
            width: '6%',
        },
        {
            title: 'Request Type',
            dataIndex: 'req_type',
            key: 'req_type',
            width: '7%',
        },
        { title: 'Domain Id', dataIndex: 'domain_id', key: 'domain_id', width: '5%' },
        { title: 'Domain Name', dataIndex: 'domain_name', key: 'domain_name', width: '25%' },
        { title: 'App Id', dataIndex: 'application_id', key: 'application_id', width: '5%' },
        {
            title: 'Comment',
            dataIndex: 'comments',
            key: 'comments',
            width: '25%',
            render: (value) => {
                return <Text className='ns-white-space-break'>{value}</Text>;
            },
        },
    ];

    const [loading, setLoading] = useState(false);
    const [tableData, setTableData] = useState([]);

    useEffect(() => {
        fetchTableData();
    }, []);

    const fetchTableData = async () => {
        setLoading(true);
        await viewDomainReq(USERNAME).then((res) => {
            if (res) {
                setTableData(res);
            }
        });
        setLoading(false);
    };

    return (
        <Table
            key={'view_domains_table'}
            loading={loading}
            rowKey={'id'}
            size='small'
            bordered
            columns={tableColumn}
            dataSource={tableData}
            pagination={false}
            scroll={{ y: 450 }}
        />
    );
};

// ReqType, domainData
const DomainReq = ({ initValues, closeModal }) => {
    if (initValues?.b_skip_for_forwarder == 0 && initValues?.b_skip_for_explicit_proxy == 0 && initValues?.b_skip_for_agent == 0) {
        initValues['steering_enabled'] = 1;
    } else {
        initValues['steering_enabled'] = 0;
    }

    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [reqType, setReqType] = useState(initValues.req_type);
    const [popUpTitle, setPopUpTitle] = useState('');
    const [openPopUp, setOpenPopUp] = useState(false);
    const [formSubmittedValues, setFormSubmittedValues] = useState({});

    const onFormChange = (changedValues, allValues) => {
        if (!allValues?.steering_enabled) {
            if (allValues.skip_app_mapping) {
                form.setFieldValue('additional_discovery_domain', -1);
            }
        }
        if (changedValues?.hasOwnProperty('req_type')) {
            setReqType(changedValues.req_type);
            if (allValues?.req_type !== 'ADD') {
                form.setFieldsValue({ domain_name: initValues?.domain_name, application_id: initValues.application_id });
            }
        }
    };
    const onFinishForm = async (values) => {
        setFormSubmittedValues(values);
        await submitForm(values);
    };

    const submitForm = async (values, skipAddCheck = false) => {
        setLoading(true);
        const postData = {
            ...values,
            comments: generateComments(values),
            req_by: USERNAME,
            domain_id: initValues?.domain_id ?? null,
            skip_add_check: skipAddCheck,
        };
        if (initValues?.domain_id) {
            postData['domain_id'] = initValues.domain_id;
        }

        await domainChangeRequest(postData).then((res) => {
            if (res?.status === 1) {
                closeModal();
            } else if (res?.status === 0) {
                message.error(res?.msg);
            } else if (res?.status === 2) {
                setPopUpTitle(res?.msg);
                setOpenPopUp(true);
            } else {
                message.error(ERROR_MSG.GENERAL_ERROR);
            }
        });
        setLoading(false);
    };

    const generateComments = (values) => {
        let comments = values.comments ?? '';
        comments += '\n--- Auto Generated ---\n';
        comments += `Ticket : ${values.ticket}\n`;
        if (initValues.steering_enabled != values.steering_enabled) {
            comments += `Steering Enabled: ${values.steering_enabled}\n`;
        }
        if (initValues.skip_app_mapping != values.skip_app_mapping) {
            comments += `Skip App Mapping: ${values.skip_app_mapping}\n`;
        }
        if (initValues.additional_discovery_domain != values.additional_discovery_domain) {
            comments += `Discovery: ${values.additional_discovery_domain}\n`;
        }
        comments += '--- End Auto Generated ---\n';
        return comments;
    };

    const onConfirmPopUp = async () => {
        await submitForm(formSubmittedValues, true);
    };

    const onCancelPopUp = () => {
        setOpenPopUp(false);
        setPopUpTitle(COMMON_STRING.BLANK);
        setFormSubmittedValues({});
    };

    return (
        <div>
            <Form
                name='domain_req'
                form={form}
                onFinish={onFinishForm}
                autoComplete={'off'}
                disabled={loading}
                size={SIZE.SMALL}
                initialValues={initValues}
                onValuesChange={onFormChange}
            >
                <Form.Item label='Request Type' name='req_type' rules={[{ required: true }]}>
                    <Select
                        // defaultValue="ADD"
                        style={{ width: 120 }}
                        // onChange={onSelectReqType}
                        options={[
                            {
                                label: 'Add',
                                value: 'ADD',
                            },
                            {
                                label: 'Edit',
                                value: 'EDIT',
                            },
                            {
                                label: 'Delete',
                                value: 'DELETE',
                            },
                        ]}
                    />
                </Form.Item>
                <Form.Item label='Domain name' name='domain_name' rules={[{ required: true }]}>
                    <Input disabled={reqType !== RequestType.ADD} />
                </Form.Item>
                <Form.Item label='Application Id' name='application_id' rules={[{ required: true }]}>
                    <Input type={'number'} disabled={reqType !== RequestType.ADD} />
                </Form.Item>
                <Form.Item
                    label='Ticket'
                    name='ticket'
                    rules={[
                        { required: true },
                        {
                            validator: (_, value) => {
                                const isValid = COMMON_REGEX.JIRA_TICKET.test(value);
                                if (!isValid) {
                                    return Promise.reject(new Error(ERROR_MSG.INVALID_JIRA_TICKET));
                                }
                                return Promise.resolve();
                            },
                        },
                    ]}
                >
                    <Input />
                </Form.Item>
                <div className='ns-flex ns-flex_space-between'>
                    <Form.Item name='steering_enabled' label='Steering Enabled' valuePropName='checked'>
                        <Switch />
                    </Form.Item>
                    <Form.Item name='skip_app_mapping' label='Skip App Mapping' valuePropName='checked'>
                        <Switch />
                    </Form.Item>
                    <Form.Item name='additional_discovery_domain' label='Additional Discovery Domain'>
                        <Radio.Group options={BitOptions} optionType='button' buttonStyle='solid' />
                    </Form.Item>
                </div>
                <Form.Item label='Comments' name='comments'>
                    <Input.TextArea rows={4} />
                </Form.Item>
                <Form.Item className='ns-flex ns-flex_centered'>
                    <Popconfirm
                        title={() => <ReqPopUpTitle msg={popUpTitle} />}
                        open={openPopUp}
                        onConfirm={onConfirmPopUp}
                        onCancel={onCancelPopUp}
                        okText={COMMON_STRING.YES}
                        cancelText={COMMON_STRING.NO}
                    ></Popconfirm>
                    <Button type='primary' htmlType='submit'>
                        {COMMON_STRING.SUBMIT}
                    </Button>
                </Form.Item>
            </Form>
        </div>
    );
};

const ReqPopUpTitle = ({ msg }) => {
    return (
        <div className='ns-flex ns-flex_column'>
            <Text>{msg}</Text>
            <Text>{COMMON_MSG.STILL_WANT_TO_REQ}</Text>
        </div>
    );
};

export default Domain;
